<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { cloneDeep, uniqBy } from 'lodash-es';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';

const props = defineProps({
  field: {
    type: String,
  },
  stage: {
    type: Object,
  },
  previous_stage_columns: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'save']);
const $t = inject('$t');
const { aggregations_map } = useBIQueryBuilder();

const field = cloneDeep(props.field);

function makeDefaultRowsFrame() {
  return {
    type: 'rows',
    preceding: 'unbounded',
    following: undefined,
  };
}

function makeDefaultRangeFrame() {
  return {
    type: 'range',
    // interval strings follow the format: 'N days' (direction is provided separately)
    precedingInterval: 'unbounded', // default for "All the above"
    precedingDirection: undefined,
    followingInterval: undefined, // default -> this row
    followingDirection: undefined,
  };
}

const field_window_config = aggregations_map[field.agg].windowConfig;
const initialFrame = field.frame ? cloneDeep(field.frame) : makeDefaultRowsFrame();
if (!initialFrame.type)
  initialFrame.type = 'rows';

// normalize older rows-based frames if switching between types
function getDefaultFrameConfig(direction = 'preceding') {
  if (field_window_config?.frame) {
    return field_window_config.frame[direction] === 0 ? undefined : field_window_config.frame[direction];
  }
  return initialFrame[direction] ?? 'unbounded';
}

if (initialFrame.type === 'rows') {
  // keep existing rows API (preceding/following) if present
  initialFrame.preceding = getDefaultFrameConfig('preceding');
  initialFrame.following = getDefaultFrameConfig('following');
}

function formatOrderBy(orderBy) {
  return orderBy.map((ob) => {
    if (typeof ob === 'string')
      return { field: ob, label: `${ob} (asc)`, direction: 'asc' };
    else
      return ob;
  });
};

const all_fields = computed(() => (uniqBy([
  ...props.previous_stage_columns.filter(col => !col.is_aggregation && !col.skip),
  ...props.stage.columns.filter(col => !col.is_aggregation && !col.skip),
], 'alias')));

function getDefaultFormData(property = 'partitionBy') {
  const fields = property === 'partitionBy' ? all_fields.value.map(field => field.alias) : all_fields.value.map(field => ({ field: field.alias, direction: 'asc' }));
  if (!field_window_config)
    return [];
  if (field_window_config[property] === 'n-1') {
    return fields.slice(0, -1);
  }
  if (field_window_config[property] === 'n') {
    return fields.slice(0, fields.length);
  }
  return [];
}

const form_data = ref({
  partitionBy: field.partitionBy || getDefaultFormData('partitionBy'),
  orderBy: formatOrderBy(field.orderBy || getDefaultFormData('orderBy')),
  frame: initialFrame,
});

// Ensure initial 'range' frame is valid for current orderBy; fallback to rows if not allowed
if (form_data.value.frame?.type === 'range' && !(() => {
  const ob = form_data.value.orderBy || [];
  // allow range only when exactly one sort field is selected and it's a date/timestamp
  if (ob.length !== 1)
    return false;
  const alias = ob[0];
  const col = props.stage?.columns?.find(c => c.alias === alias);
  let ft = col?.field_type;
  if (!col)
    ft = props.previous_stage_columns.find(c => c.alias === alias)?.type;
  return ft === 'date' || ft === 'timestamp';
})()) {
  form_data.value.frame = makeDefaultRowsFrame();
}

const frameModes = [
  { label: 'Rows', value: 'rows' },
  { label: 'Range', value: 'range' },
];

function isRangeAllowed() {
  const ob = form_data.value.orderBy || [];
  // allow range only when exactly one sort field is selected and it's a date/timestamp
  if (ob.length !== 1)
    return false;
  const alias = ob[0]?.field;
  const col = props.stage?.columns?.find(c => c.alias === alias);
  let ft = col?.field_type;
  if (!col)
    ft = props.previous_stage_columns.find(c => c.alias === alias)?.type;
  return ft === 'date' || ft === 'timestamp';
}

const availableFrameModes = computed(() => {
  const modes = [{ label: 'Rows', value: 'rows' }];
  if (isRangeAllowed())
    modes.push({ label: 'Range', value: 'range' });
  return modes;
});

// Rows mode options (existing)
const betweenOptions = [
  { label: 'All above rows', value: 'unbounded' },
  { label: 'Above n rows', value: 10 },
  { label: 'This row', value: undefined },
];

const andOptions = [
  { label: 'All below rows', value: 'unbounded' },
  { label: 'Below n rows', value: 10 },
  { label: 'This row', value: undefined },
];

// Range mode: selection types for preceding/following
const rangeBetweenOptions = [
  { label: 'All above rows', value: 'unbounded' },
  { label: 'Previous n', value: 'previous' },
  { label: 'Next n', value: 'next' },
  { label: 'This row', value: 'this' },
];

const rangeAndOptions = [
  { label: 'All below rows', value: 'unbounded' },
  { label: 'Next n', value: 'next' },
  { label: 'Previous n', value: 'previous' },
  { label: 'This row', value: 'this' },
];

const rangeUnits = [
  { label: 'Days', value: 'days' },
  { label: 'Weeks', value: 'weeks' },
  { label: 'Months', value: 'months' },
];

// helpers to manage range input state (counts & units)
// try to hydrate count/unit from any existing saved interval strings like "2 months"
function parseCountUnit(interval) {
  if (!interval || interval === 'unbounded')
    return null;
  const parts = String(interval).split(' ').filter(Boolean);
  const count = Number(parts[0]);
  const unit = parts[1] || 'days';
  return {
    count: Number.isNaN(count) ? 1 : count,
    unit,
  };
}

let initPrecedingCount = 1;
let initPrecedingUnit = 'days';
let initPrecedingSelection = 'unbounded';
let initFollowingCount = 1;
let initFollowingUnit = 'days';
let initFollowingSelection = 'this';

if (form_data.value.frame?.type === 'range') {
  const pInterval = form_data.value.frame.precedingInterval;
  const pDir = form_data.value.frame.precedingDirection;
  if (pInterval === 'unbounded') {
    initPrecedingSelection = 'unbounded';
  }
  else if (pInterval === undefined) {
    initPrecedingSelection = 'this';
  }
  else {
    const parsed = parseCountUnit(pInterval);
    if (parsed) {
      initPrecedingCount = parsed.count;
      initPrecedingUnit = parsed.unit;
    }
    initPrecedingSelection = (pDir === 'FOLLOWING') ? 'next' : 'previous';
  }

  const fInterval = form_data.value.frame.followingInterval;
  const fDir = form_data.value.frame.followingDirection;
  if (fInterval === 'unbounded') {
    initFollowingSelection = 'unbounded';
  }
  else if (fInterval === undefined) {
    initFollowingSelection = 'this';
  }
  else {
    const parsedF = parseCountUnit(fInterval);
    if (parsedF) {
      initFollowingCount = parsedF.count;
      initFollowingUnit = parsedF.unit;
    }
    initFollowingSelection = (fDir === 'PRECEDING') ? 'previous' : 'next';
  }
}

const precedingCount = ref(initPrecedingCount);
const precedingUnit = ref(initPrecedingUnit);
const precedingSelection = ref(initPrecedingSelection);

const followingCount = ref(initFollowingCount);
const followingUnit = ref(initFollowingUnit);
const followingSelection = ref(initFollowingSelection);

function setFrameType(type) {
  if (type === form_data.value.frame.type)
    return;
  if (type === 'range') {
    // Prevent switching to range unless the last orderBy field is date/timestamp
    if (!isRangeAllowed())
      return;
    form_data.value.frame = makeDefaultRangeFrame();
    precedingSelection.value = 'unbounded';
    precedingCount.value = 1;
    precedingUnit.value = 'days';
    followingSelection.value = 'this';
    followingCount.value = 1;
    followingUnit.value = 'days';
  }
  else {
    form_data.value.frame = makeDefaultRowsFrame();
  }
}

// Update preceding interval based on selection + inputs
function updatePrecedingSelection(value) {
  precedingSelection.value = value;
  if (value === 'unbounded') {
    form_data.value.frame.precedingInterval = 'unbounded';
    form_data.value.frame.precedingDirection = undefined;
  }
  else if (value === 'this') {
    form_data.value.frame.precedingInterval = undefined;
    form_data.value.frame.precedingDirection = undefined;
  }
  else if (value === 'previous') {
    // positive interval (direction implied as PRECEDING)
    form_data.value.frame.precedingInterval = `${precedingCount.value} ${precedingUnit.value}`;
    form_data.value.frame.precedingDirection = undefined;
  }
  else if (value === 'next') {
    // positive interval; mark direction as FOLLOWING
    form_data.value.frame.precedingInterval = `${precedingCount.value} ${precedingUnit.value}`;
    form_data.value.frame.precedingDirection = 'FOLLOWING';
  }
}

// Update following interval based on selection + inputs
function updateFollowingSelection(value) {
  followingSelection.value = value;
  if (value === 'unbounded') {
    form_data.value.frame.followingInterval = 'unbounded';
    form_data.value.frame.followingDirection = undefined;
  }
  else if (value === 'this') {
    form_data.value.frame.followingInterval = undefined;
    form_data.value.frame.followingDirection = undefined;
  }
  else if (value === 'next') {
    // positive interval (direction implied as FOLLOWING)
    form_data.value.frame.followingInterval = `${followingCount.value} ${followingUnit.value}`;
    form_data.value.frame.followingDirection = undefined;
  }
  else if (value === 'previous') {
    // positive interval; mark direction as PRECEDING
    form_data.value.frame.followingInterval = `${followingCount.value} ${followingUnit.value}`;
    form_data.value.frame.followingDirection = 'PRECEDING';
  }
}

// Ensure numeric inputs never go below 1
function clampToPositive(n) {
  const v = Number(n);
  return (Number.isNaN(v) || v < 1) ? 1 : v;
}

function onRowsPrecedingInput(e) {
  // keep rows >= 1
  let val = Number(e.target.value);
  if (Number.isNaN(val) || val < 1)
    val = 1;
  form_data.value.frame.preceding = val;
}

function onRowsFollowingInput(e) {
  let val = Number(e.target.value);
  if (Number.isNaN(val) || val < 1)
    val = 1;
  form_data.value.frame.following = val;
}

function onPrecedingCountInput(e) {
  precedingCount.value = clampToPositive(e.target.value);
  // update derived frame state
  updatePrecedingSelection(precedingSelection.value);
}

function onFollowingCountInput(e) {
  followingCount.value = clampToPositive(e.target.value);
  updateFollowingSelection(followingSelection.value);
}
function clearSettings() {
  emit('save', {});
}

function save() {
  emit('save', form_data.value);
}

function updateDirection(option) {
  const index = form_data.value.orderBy.findIndex(item => item.field === option.field);
  if (index !== -1) {
    form_data.value.orderBy[index].direction = option.direction === 'asc' ? 'desc' : 'asc';
    option.direction = form_data.value.orderBy[index].direction;
  }
}

function getDirection(option) {
  const index = form_data.value.orderBy.findIndex(item => item.field === option.field);
  if (index !== -1) {
    return form_data.value.orderBy[index].direction;
  }
  return 'asc';
}
</script>

<template>
  <hawk-modal-container id="bi-field-settings" content_class="w-[690px] rounded-lg" :options="props.modalOptions">
    <Vueform v-model="form_data" sync size="sm" :display-errors="false">
      <div class="col-span-12">
        <div class="flex items-center p-6 justify-between text-lg font-semibold text-gray-800 ">
          <div>
            {{ field.alias }}
          </div>
          <div class="flex items-center justify-center">
            <div class="text-gray-600 rounded-md hover:bg-gray-50 cursor-pointer flex justify-center items-center p-2" @click="emit('close')">
              <IconHawkXClose class="w-6 h-6" />
            </div>
          </div>
        </div>
        <hawk-modal-content class="border-t">
          <div class="grid gap-2">
            <div>
              <div class="text-sm font-medium text-gray-700">
                {{ $t('Breakdown By') }}
              </div>
              <div class="max-w-[640px]">
                <TagsElement
                  name="partitionBy"
                  :items="all_fields"
                  append-to="#bi-field-settings"
                  :default="[]"
                  :can-clear="true"
                  label-prop="alias"
                  value-prop="alias"
                />
              </div>
            </div>
            <div>
              <div class="text-sm font-medium text-gray-700 mt-4">
                {{ $t('Sort By') }}
              </div>
              <div class="max-w-[640px]">
                <TagsElement
                  name="orderBy"
                  :items="all_fields.map((field) => ({ field: field.alias, direction: 'asc' }))"
                  append-to="#bi-field-settings"
                  :default="[]"
                  :can-clear="true"
                  value-prop="field"
                  label-prop="field"
                  :object="true"
                  :disabled="form_data.frame.type === 'range'"
                >
                  <template #tag="{ option, handleTagRemove, disabled, el$ }">
                    <div
                      class="TagsElement.select.tag--> form-bg-tag form-color-tag form-border-width-tag form-border-color-tag font-semibold flex items-center whitespace-nowrap min-w-0 rtl:pl-0 rtl:mr-0 !bg-transparent !text-gray-700 !font-normal !text-sm !border-gray-300 !leading-relaxed TagsElement.select.tag_sm--> form-radius-input-tag-sm form-text-small-sm form-py-tag-sm form-pl-tag-sm form-mr-space-tags-sm form-mb-space-tags-sm rtl:form-pr-tag-sm rtl:form-ml-space-tags-sm" :class="{
                        'multiselect-tag-disabled': disabled,
                      }"
                      @click="updateDirection(option, el$);"
                    >
                      <span class="TagsElement.select.tagWrapper--> overflow-hidden overflow-ellipsis">
                        {{ option.field }}
                      </span>
                      <span v-if="getDirection(option) === 'asc'">
                        <IconHawkArrowUp class="text-gray-500" />
                      </span>
                      <span v-else>
                        <IconHawkArrowDown class="text-gray-500" />
                      </span>
                      <span
                        v-if="!disabled"
                        class="TagsElement.select.tagRemove--> flex items-center justify-center p-1 mx-0.5 hover:bg-black hover:bg-opacity-10 group text-gray-400 !p-0.5 !rounded hover:!bg-gray-100 !ml-1 TagsElement.select.tagRemove_sm--> form-radius-input-tag-sm"
                        @mousedown.prevent="handleTagRemove(option, $event)"
                      >
                        <span class="TagsElement.select.tagRemoveIcon--> mask-bg mask-form-remove bg-current inline-block w-3 h-3" />
                      </span>
                    </div>
                  </template>
                </TagsElement>
              </div>
            </div>
            <div>
              <div class="text-sm font-medium text-gray-700 mt-4">
                {{ $t('Frame') }}
              </div>
              <div>
                <div class="flex items-center gap-3">
                  <hawk-menu
                    :items="availableFrameModes"
                    position="fixed"
                    additional_trigger_classes="!ring-0"
                    @select="setFrameType($event.value)"
                  >
                    <template #trigger="{ open }">
                      <div class="border-b border-gray-300 pb-1 cursor-pointer flex items-center gap-2 text-sm">
                        <span>{{ frameModes.find(m => m.value === form_data.frame.type)?.label || form_data.frame.type }}</span>
                        <IconHawkChevronDown class="w-4 h-4 text-gray-600 transition-transform" :class="{ 'rotate-180': open }" />
                      </div>
                    </template>
                  </hawk-menu>

                  <div class="text-sm text-gray-600">
                    {{ $t('Between') }}
                  </div>

                  <div v-if="form_data.frame.type === 'rows'">
                    <hawk-menu
                      :items="betweenOptions"
                      additional_trigger_classes="!ring-0"
                      position="fixed"
                      @select="form_data.frame.preceding = $event.value"
                    >
                      <template #trigger="{ open }">
                        <div class="border-b border-gray-300 pb-1 cursor-pointer flex items-center gap-2 text-sm">
                          <span v-if="form_data.frame.preceding !== undefined && form_data.frame.preceding !== 'unbounded'">
                            {{ $t('Above') }}
                            <input
                              v-model.number="form_data.frame.preceding"
                              type="number"
                              min="1"
                              class="w-12 ml-2 px-2 py-1 border rounded text-sm"
                              @input="onRowsPrecedingInput($event)"
                              @click.stop
                              @keyup.stop
                              @keydown.stop
                            >
                            {{ $t('Rows') }}
                          </span>
                          <span v-else>{{ betweenOptions.find(m => m.value === form_data.frame.preceding)?.label || form_data.frame.preceding }}</span>
                          <IconHawkChevronDown class="w-4 h-4 text-gray-600 transition-transform" :class="{ 'rotate-180': open }" />
                        </div>
                      </template>
                    </hawk-menu>
                  </div>

                  <div v-else>
                    <!-- Range preceding selection -->
                    <hawk-menu
                      :items="rangeBetweenOptions"
                      additional_trigger_classes="!ring-0"
                      position="fixed"
                      @select="updatePrecedingSelection($event.value)"
                    >
                      <template #trigger="{ open }">
                        <div class="border-b border-gray-300 pb-1 cursor-pointer flex items-center gap-2 text-sm">
                          <span v-if="precedingSelection === 'previous'">
                            {{ $t('Previous') }}
                            <input
                              v-model.number="precedingCount"
                              type="number"
                              min="1"
                              class="w-12 ml-2 px-2 py-1 border rounded text-sm"
                              @input="onPrecedingCountInput($event)"
                              @click.stop
                              @keyup.stop
                              @keydown.stop
                            >
                            <select v-model="precedingUnit" class="ml-2 px-2 py-1 border rounded text-sm" @change="updatePrecedingSelection(precedingSelection)" @click.stop>
                              <option v-for="u in rangeUnits" :key="u.value" :value="u.value">{{ u.label }}</option>
                            </select>
                          </span>
                          <span v-else-if="precedingSelection === 'next'">
                            {{ $t('Next') }}
                            <input
                              v-model.number="precedingCount"
                              type="number"
                              min="1"
                              class="w-12 ml-2 px-2 py-1 border rounded text-sm"
                              @input="onPrecedingCountInput($event)"
                              @click.stop
                              @keyup.stop
                              @keydown.stop
                            >
                            <select v-model="precedingUnit" class="ml-2 px-2 py-1 border rounded text-sm" @change="updatePrecedingSelection(precedingSelection)" @click.stop>
                              <option v-for="u in rangeUnits" :key="u.value" :value="u.value">{{ u.label }}</option>
                            </select>
                          </span>
                          <span v-else>
                            {{ rangeBetweenOptions.find(m => m.value === precedingSelection)?.label || precedingSelection }}
                          </span>
                          <IconHawkChevronDown class="w-4 h-4 text-gray-600 transition-transform" :class="{ 'rotate-180': open }" />
                        </div>
                      </template>
                    </hawk-menu>
                  </div>

                  <div class="text-sm text-gray-600">
                    {{ $t('AND') }}
                  </div>

                  <div v-if="form_data.frame.type === 'rows'">
                    <hawk-menu
                      :items="andOptions"
                      additional_trigger_classes="!ring-0"
                      position="fixed"
                      @select="form_data.frame.following = $event.value"
                    >
                      <template #trigger="{ open }">
                        <div class="border-b border-gray-300 pb-1 cursor-pointer flex items-center gap-2 text-sm">
                          <span v-if="form_data.frame.following !== undefined && form_data.frame.following !== 'unbounded'">
                            {{ $t('Below') }}

                            <input
                              v-model.number="form_data.frame.following"
                              type="number"
                              min="1"
                              class="w-20 ml-2 px-2 py-1 border rounded text-sm"
                              @input="onRowsFollowingInput($event)"
                              @click.stop
                              @keyup.stop
                              @keydown.stop
                            >
                            {{ $t('Rows') }}

                          </span>
                          <span v-else>{{ andOptions.find(m => m.value === form_data.frame.following)?.label || form_data.frame.following }}</span>
                          <IconHawkChevronDown class="w-4 h-4 text-gray-600 transition-transform" :class="{ 'rotate-180': open }" />
                        </div>
                      </template>
                    </hawk-menu>
                  </div>

                  <div v-else>
                    <!-- Range following selection -->
                    <hawk-menu
                      :items="rangeAndOptions"
                      additional_trigger_classes="!ring-0"
                      position="fixed"
                      @select="updateFollowingSelection($event.value)"
                    >
                      <template #trigger="{ open }">
                        <div class="border-b border-gray-300 pb-1 cursor-pointer flex items-center gap-2 text-sm">
                          <span v-if="followingSelection === 'next'">
                            {{ $t('Next') }}
                            <input
                              v-model.number="followingCount"
                              type="number"
                              min="1"
                              class="w-12 ml-2 px-2 py-1 border rounded text-sm"
                              @input="onFollowingCountInput($event)"
                              @click.stop
                              @keyup.stop
                              @keydown.stop
                            >
                            <select v-model="followingUnit" class="ml-2 px-2 py-1 border rounded text-sm" @change="updateFollowingSelection(followingSelection)" @click.stop>
                              <option v-for="u in rangeUnits" :key="u.value" :value="u.value">{{ u.label }}</option>
                            </select>
                          </span>
                          <span v-else-if="followingSelection === 'previous'">
                            {{ $t('Previous') }}
                            <input
                              v-model.number="followingCount"
                              type="number"
                              min="1"
                              class="w-12 ml-2 px-2 py-1 border rounded text-sm"
                              @input="onFollowingCountInput($event)"
                              @click.stop
                              @keyup.stop
                              @keydown.stop
                            >
                            <select v-model="followingUnit" class="ml-2 px-2 py-1 border rounded text-sm" @change="updateFollowingSelection(followingSelection)" @click.stop>
                              <option v-for="u in rangeUnits" :key="u.value" :value="u.value">{{ u.label }}</option>
                            </select>
                          </span>
                          <span v-else>
                            {{ rangeAndOptions.find(m => m.value === followingSelection)?.label || followingSelection }}
                          </span>
                          <IconHawkChevronDown class="w-4 h-4 text-gray-600 transition-transform" :class="{ 'rotate-180': open }" />
                        </div>
                      </template>
                    </hawk-menu>
                  </div>
                </div>
                <HiddenElement name="frame" :meta="true" />
              </div>
            </div>
          </div>
        </hawk-modal-content>
        <hawk-modal-footer class="flex justify-between items-center">
          <template #left>
            <HawkButton color="error" type="outlined" class="mr-3" @click="clearSettings">
              {{ $t('Clear') }}
            </HawkButton>
          </template>
          <template #right>
            <div class="flex justify-end">
              <HawkButton color="primary" type="outlined" class="mr-3" @click="$emit('close')">
                {{ $t('Cancel') }}
              </HawkButton>
              <HawkButton
                @click="save"
              >
                {{ $t('Save') }}
              </HawkButton>
            </div>
          </template>
        </hawk-modal-footer>
      </div>
    </Vueform>
  </hawk-modal-container>
</template>
